import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/Icon';
import ModalContent from './ModalContent';
import ModalContainer from '../../shared/modalContainer/ModalContainer';

const VideoIcon = () => (
  <div className="absolute top-1/2 left-1/2 w-10 h-10 bg-primary-500 text-white transform -translate-x-1/2 -translate-y-1/2 z-10 flex justify-center items-center text-2xl rounded-full hover:bg-primary-600">
    ▸
  </div>
);

const MediaAssets = ({
  media,
  mediaLength,
  small = false,
  thumbSize,
  slider,
  handleDataLayer,
}) => {
  const [active, setActive] = useState(null);

  const toggleScrollLock = (close = false) => {
    if (close) {
      document.querySelector('html').classList.remove('scroll-lock');
    } else {
      document.querySelector('html').classList.toggle('scroll-lock');
    }
  };

  useEffect(() => {
    if (typeof active === 'number') {
      toggleScrollLock();
    } else {
      toggleScrollLock(true);
    }

    return () => {
      toggleScrollLock(true);
    };
  }, [active]);

  const nextImg = () => {
    let newActive = 0;
    if (active === media.length - 1) {
      newActive = 0;
    } else {
      newActive = active + 1;
    }

    const mediaObj = media[newActive];
    setActive(newActive);

    handleDataLayer({
      event_name: 'media',
      event_action: 'Media next',
      system_category: mediaObj.category,
      system_name: mediaObj.systemName,
      media_type: mediaObj.type,
      media_name: mediaObj.alt,
    });
  };

  const prevImg = () => {
    let newActive = 0;
    if (active === 0) {
      newActive = media.length - 1;
    } else {
      newActive = active - 1;
    }

    const mediaObj = media[newActive];
    setActive(newActive);

    handleDataLayer({
      event_name: 'media',
      event_action: 'Media prev',
      system_category: mediaObj.category,
      system_name: mediaObj.systemName,
      media_type: mediaObj.type,
      media_name: mediaObj.alt,
    });
  };

  const mediaTypeSortOrder = {
    VIDEO: 1,
    PITCH: 2,
    SCREENSHOT: 3,
    DOCUMENT: 4,
  };

  // Temp fix for size on thumbnails
  const thumbSizeClass =
    thumbSize === 'lg' ? `w-[340px] h-[200px]` : 'w-[170px] h-[100px]';

  return (
    <>
      {(typeof active === 'number' && (
        <ModalContainer imageObj={media[active].url} setActive={setActive}>
          <>
            <ModalContent mediaObj={media[active]} />
            {mediaLength > 1 && (
              <div className="flex justify-between left-2 right-2 absolute top-1/2">
                <button
                  type="button"
                  name="prev screenshot"
                  aria-label="Previous screenshot"
                  onClick={prevImg}
                  className="bg-[#e9f4fb] border border-[#1a6699] rounded-sm shadow-none text-[#1a6699] cursor-pointer leading-[1px] p-2"
                >
                  <Icon fill="currentColor" name="arrow--left" width="20" />
                </button>
                <button
                  type="button"
                  name="next screenshot"
                  aria-label="Next screenshot"
                  onClick={nextImg}
                  className="bg-[#e9f4fb] border border-[#1a6699] rounded-sm shadow-none text-[#1a6699] cursor-pointer leading-[1px] p-2"
                >
                  <Icon fill="currentColor" name="arrow--right" width="20" />
                </button>
              </div>
            )}
          </>
        </ModalContainer>
      )) ||
        null}

      {media
        .sort((mediaObjA, mediaObjB) => {
          if (!mediaObjA.type || !mediaObjB.type) {
            return 0;
          }
          if (
            !mediaTypeSortOrder[mediaObjA.type] ||
            !mediaTypeSortOrder[mediaObjB.type]
          ) {
            return 0;
          }

          const typeOrder =
            mediaTypeSortOrder[mediaObjA.type] -
            mediaTypeSortOrder[mediaObjB.type];

          if (
            typeOrder === 0 &&
            mediaObjA?.order >= 0 &&
            mediaObjB?.order >= 0
          ) {
            return mediaObjA.order - mediaObjB.order;
          }

          return typeOrder;
        })
        .map((mediaObj, index) => (
          <button
            key={mediaObj?.id}
            type="button"
            aria-label={`Click to show ${mediaObj?.type} in modal`}
            onClick={() => {
              setActive(index);

              handleDataLayer({
                event_name: 'media',
                event_action: 'Media click',
                system_category: mediaObj.category,
                system_name: mediaObj.systemName,
                media_type: mediaObj.type,
                media_name: mediaObj.alt,
              });
            }}
            className={`flex bg-transparent cursor-pointer border-0 flex-shrink-0 ${
              slider ? 'px-2' : 'px-0'
            }`}
          >
            {mediaObj?.type === 'VIDEO' && (
              <div className="relative">
                <VideoIcon />
                <img
                  alt={mediaObj?.alt}
                  src={mediaObj?.url}
                  loading="lazy"
                  className={`rounded-lg ${thumbSizeClass} align-middle`}
                />
              </div>
            )}
            {mediaObj?.type === 'PITCH' && (
              <div
                className={`relative pb-[56.25%] h-0 ${
                  small ? 'w-[170px]' : 'w-[300px]'
                }`}
              >
                <VideoIcon />
                <img
                  alt={mediaObj?.alt}
                  key={mediaObj?.id}
                  src={mediaObj?.url}
                  loading="lazy"
                  className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                />
              </div>
            )}
            {mediaObj?.type === 'SCREENSHOT' && (
              <img
                alt={mediaObj?.alt}
                src={mediaObj?.url}
                loading="lazy"
                className={`rounded-lg ${thumbSizeClass} align-middle`}
              />
            )}
            {mediaObj?.type === 'DOCUMENT' && (
              <div
                className={`flex flex-col justify-center items-center font-bold m-1 rounded-lg ${thumbSizeClass} bg-[#0084d2cc] text-white`}
              >
                <span className="font-bold text-clamp-xl mb-1">PDF</span>
                <span className="font-light text-clamp-xs">
                  {mediaObj.alt || mediaObj.name}
                </span>
              </div>
            )}
          </button>
        ))}
    </>
  );
};

export default MediaAssets;
